import styles from './NoContent.module.scss';
import cx from 'classnames';
import { Text } from 'components';
import { Icon, Icons } from 'components/Icon/Icon';

type NoContentKey =
  | 'patientsList'
  | 'patientsListWithFilter'
  | 'dashboardList'
  | 'activityFeedList'
  | 'dashboardFeed'
  | 'discussion'
  | 'discussionImages'
  | 'discussionLinks'
  | 'discussionFiles'
  | 'patientProfile';

type Props = {
  pageKey: NoContentKey;
  className?: string;
  hideIcon?: boolean;
};

const mapKeyToContent: Record<
  NoContentKey,
  {
    icon: Icons;
    title: string;
    text: string;
  }
> = {
  patientsList: {
    icon: 'NoUser',
    title: 'No patients found',
    text: 'Currently, there are no patient records available in the system',
  },
  patientsListWithFilter: {
    icon: 'NoFilter',
    title: 'No results found',
    text: 'Adjust filters or try a different combination',
  },
  dashboardList: {
    icon: 'NoUser',
    title: 'No matching patients',
    text: 'There are currently no new patients based on the applied filter',
  },
  dashboardFeed: {
    icon: 'NoMessage',
    title: 'No new messages',
    text: 'There are currently no new messages in the message feed with applied filter. Check back later for updates or send a message from the patient case page to initiate communication',
  },
  discussion: {
    icon: 'NoMessage',
    title: 'No new messages',
    text: 'Currently, there are no new messages in the discussion. Stay tuned for updates or start a new conversation',
  },
  discussionImages: {
    icon: 'NoImage',
    title: 'No attached images',
    text: 'There are currently no images attached to the discussion',
  },
  discussionLinks: {
    icon: 'NoLink',
    title: 'No attached links',
    text: 'There are currently no links attached to the discussion',
  },
  discussionFiles: {
    icon: 'NoFiles',
    title: 'No attached files',
    text: 'Currently, there are no files attached to the discussion',
  },
  activityFeedList: {
    icon: 'NoNotes',
    title: 'No updated tasks',
    text: 'Currently, there are no tasks that have been updated',
  },
  patientProfile: {
    icon: 'NoUser',
    title: 'No patient selected',
    text: 'Please select a patient to view or manage their information. Start by searching or navigating to the patient list',
  },
};

export const NoContent = ({ pageKey, className, hideIcon = false }: Props) => {
  return (
    <div className={cx(styles.container, className)}>
      {!hideIcon && <Icon className={styles.icon} name={mapKeyToContent[pageKey].icon} />}
      <Text type="body-big">{mapKeyToContent[pageKey].title}</Text>
      <Text type="body2">{mapKeyToContent[pageKey].text}</Text>
    </div>
  );
};
